import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { snowflakeApi } from '../utils/api';
import { useWorkflowContext } from '../context/WorkflowContext';
import {
  Stepper,
  Step,
  StepLabel,
  Paper,
  Typography,
  Box,
  StepButton,
  Tooltip,
  useTheme,
  styled,
  IconButton,
  alpha,
  SxProps,
  Theme,
  CircularProgress
} from '@mui/material';
import {
  BarChart as Module1Icon,
  Category as Module2Icon,
  Hub as Module3Icon,
  Settings as Module4Icon,
  Search as SerpAnalysisIcon, // Add icon for SERP analysis
  Explore as ExploreIcon,
  Work as JobMonitorIcon,
  TrendingUp as DataForSeoIcon, // Add icon for DataForSEO
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon
} from '@mui/icons-material';

interface WorkflowStepperProps {
  activeStep: number;
  workflowId?: string;
  clientId?: string;
  clientName?: string;
  workflowName?: string; // Add workflowName for display
  moduleId?: number; // Add moduleId for job navigation
  moduleCompletionStatus?: {
    module1: boolean;
    module2: boolean;
    module3: boolean;
    module4: boolean;
    serpAnalysis?: boolean;
    dataForSeoAnalysis?: boolean;
    explore?: boolean;
  };
  onCollapseChange?: (collapsed: boolean) => void;
  initialCollapsed?: boolean;
  sx?: SxProps<Theme>; // Add sx prop for custom styling
}

interface ModuleStatusInfo {
  workflow_id: number;
  modules: Array<{
    module_id: number;
    module_type: string;
    status: string;
    error: string | null;
    created_at: string;
    updated_at: string;
    step_number: number;
  }>;
  latest_status_by_type: Record<string, {
    module_id: number;
    module_type: string;
    status: string;
    error: string | null;
    created_at: string;
    updated_at: string;
    step_number: number;
  }>;
  step_statuses: Record<string, {
    status: string;
    has_error: boolean;
    error: string | null;
    module_type: string;
    completed: boolean;
    failed: boolean;
    in_progress: boolean;
    updated_at: string;
  }>;
  error?: string;
}

interface JobAvailabilityInfo {
  hasJobs: boolean;
  jobParams?: {
    externalJobId: string;
    moduleId: number;
    workflowId: number;
    clientId: number;
  };
  moduleType?: string;
  error?: string;
}

const VerticalStepperContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  position: 'sticky',
  top: theme.spacing(2),
  maxHeight: 'calc(100vh - 40px)',
  overflowY: 'auto',
  width: '280px', // Fixed width instead of calculated
  minWidth: '280px', // Ensure consistent minimum width
  maxWidth: '280px', // Keep max width
  marginRight: theme.spacing(0),
  transition: theme.transitions.create(['background-color', 'box-shadow'], { // Removed width from transition
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.1), 0 7px 10px -5px rgba(0, 0, 0, 0.04)',
  '&:hover': {
    boxShadow: '0 6px 22px 0 rgba(0, 0, 0, 0.12), 0 8px 12px -6px rgba(0, 0, 0, 0.06)',
  },
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  zIndex: 10, // Add z-index to control stacking order
}));

const CollapsedStepperContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'sticky',
  top: theme.spacing(2),
  maxHeight: 'calc(100vh - 40px)',
  overflowY: 'auto',
  width: '60px',
  transition: theme.transitions.create(['width', 'margin', 'background-color', 'box-shadow'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.1), 0 7px 10px -5px rgba(0, 0, 0, 0.04)',
  '&:hover': {
    boxShadow: '0 6px 22px 0 rgba(0, 0, 0, 0.12), 0 8px 12px -6px rgba(0, 0, 0, 0.06)',
  },
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  zIndex: 10, // Add z-index to match expanded container
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  transition: theme.transitions.create(['transform', 'background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const CompletedStepIcon = styled(Box)(({ theme }) => ({
  border: `2px solid ${theme.palette.success.dark}`,
  borderRadius: '50%',
  padding: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.success.dark, 0.1),
}));

const GradientTypography = styled(Typography)(() => ({
  fontWeight: 600,
  // DAC Group gradient for professional branding
  background: 'linear-gradient(45deg, #332E5C, #00CED1)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}));

// Component for showing processing state with spinning progress around icon
const ProcessingStepIcon: React.FC<{ 
  icon: React.ReactNode; 
  isProcessing: boolean; 
  size?: number;
}> = ({ icon, isProcessing, size = 24 }) => {
  const theme = useTheme();
  
  if (!isProcessing) {
    return <>{icon}</>;
  }
  
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        size={size + 8}
        thickness={2}
        sx={{
          color: theme.palette.warning.main,
          position: 'absolute',
          top: -4,
          left: -4,
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          '& svg': {
            color: theme.palette.warning.main,
          },
        }}
      >
        {icon}
      </Box>
    </Box>
  );
};

// Custom step icon for the expanded stepper that shows processing state
const CustomStepIcon: React.FC<{
  icon: React.ReactNode;
  active: boolean;
  completed: boolean;
  inProgress: boolean;
  stepNumber: number;
}> = ({ icon, active, completed, inProgress, stepNumber }) => {
  const theme = useTheme();
  
  if (inProgress) {
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          size={28}
          thickness={2}
          sx={{
            color: theme.palette.warning.main,
            position: 'absolute',
            top: -2,
            left: -2,
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: theme.palette.warning.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 'bold',
          }}
        >
          {stepNumber}
        </Box>
      </Box>
    );
  }
  
  return null; // Let MUI handle normal states
};

// Helper function to determine if a step should be disabled
const isStepDisabled = (
  index: number,
  workflowId: string | undefined,
  moduleStatuses: ModuleStatusInfo | null,
  hasModule4Config: any,
  hasModule3: any
): boolean => {
  if (!workflowId) {
    return true;
  }
  
  // Check if there's a record for this step (1-based step numbers)
  const stepNumber = index + 1;
  const hasStepRecord = moduleStatuses?.step_statuses?.[String(stepNumber)];
  
  // Step 1, 7, 8 or has record: always enabled
  if (index === 0 || index === 6 || index === 7 || hasStepRecord) {
    return false;
  }
  
  // Otherwise check if previous step is completed
  const prevStepStatus = moduleStatuses?.step_statuses?.[String(index)]; // Previous step (index is 0-based, step numbers are 1-based)
  const prevStepCompleted = prevStepStatus?.completed || 
                           (hasModule4Config && (index - 1) <= 3) || 
                           (hasModule3 && ((index - 1) === 1 || (index - 1) === 2)) || // Steps 2-3 complete if module3 exists
                           false;
  
  return !prevStepCompleted;
};

// Function to generate dynamic steps based on module statuses
const getSteps = (moduleStatuses: ModuleStatusInfo | null) => {
  // Check if step 5 (module4) is completed
  const step5Status = moduleStatuses?.step_statuses?.['5'];
  const isStep5Completed = step5Status?.completed || false;

  return [
    {
      label: 'Keyword Analysis',
      description: 'Analyze keywords and identify topics',
      icon: <Module1Icon />,
      path: 'module1'
    },
    {
      label: 'Keyword Classification',
      description: 'Classify keywords with topics',
      icon: <Module2Icon />,
      path: 'module2'
    },
    {
      label: 'Semantic Analysis',
      description: 'Perform semantic analysis',
      icon: <Module3Icon />,
      path: 'module3'
    },
    {
      label: 'Batch Configuration',
      description: 'Configure batch processing',
      icon: <Module4Icon />,
      path: 'module4'
    },
    {
      label: isStep5Completed ? 'Batch Results' : 'Job Monitoring',
      description: isStep5Completed ? 'Batch Keyword Classification' : 'Monitor processing jobs',
      icon: <JobMonitorIcon />,
      path: 'jobs'
    },
    {
      label: 'SERP Analysis',
      description: 'Analyze search engine results',
      icon: <SerpAnalysisIcon />,
      path: 'serp-tool'
    },
    {
      label: 'Data For SEO Analysis',
      description: 'Get search volume data',
      icon: <DataForSeoIcon />,
      path: 'data-for-seo-tool'
    },
    {
      label: 'Explore Results',
      description: 'Explore and analyze results',
      icon: <ExploreIcon />,
      path: 'explore'
    }
  ];
};

const WorkflowStepper: React.FC<WorkflowStepperProps> = ({
  activeStep,
  workflowId: propWorkflowId,
  clientId: propClientId,
  clientName: propClientName,
  workflowName: propWorkflowName,
  moduleId, // Include moduleId from props
  moduleCompletionStatus,
  onCollapseChange,
  initialCollapsed = true,
  sx // Add sx prop
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Use context data if available, fallback to props
  const contextData = useWorkflowContext();
  const workflowId = contextData.workflowId || propWorkflowId;
  const clientId = contextData.clientId || propClientId;
  const clientName = contextData.clientName || propClientName;
  const { setModuleStatuses: setContextModuleStatuses } = contextData;
  const workflowName = contextData.workflowName || propWorkflowName;
  const refreshTrigger = contextData.refreshTrigger;
  
  // Get the initial collapsed state from localStorage or fallback to prop
  const getInitialCollapsedState = () => {
    try {
      const savedState = localStorage.getItem('workflowStepperCollapsed');
      // If we have a saved state, use it; otherwise use the initialCollapsed prop
      return savedState !== null ? savedState === 'true' : initialCollapsed;
    } catch (error) {
      // If localStorage is not available (e.g., private browsing), use the prop
      console.warn('Failed to read from localStorage:', error);
      return initialCollapsed;
    }
  };
  
  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState());
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const [moduleStatuses, setModuleStatuses] = useState<ModuleStatusInfo | null>(null);
  const [isLoadingModuleStatuses, setIsLoadingModuleStatuses] = useState<boolean>(false);

  // Apply initial collapse state on mount - only run once
  useEffect(() => {
    // Notify parent component about the actual initial state
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
    // Empty dependency array means this only runs on mount
  }, []);

  // Fetch module statuses when component mounts or workflowId changes
  useEffect(() => {
    const fetchModuleStatuses = async () => {
      // Only fetch if we have a workflowId (UUID format)
      if (!workflowId) {
        return;
      }

      setIsLoadingModuleStatuses(true);
      
      try {
        console.log(`Fetching module statuses for workflow: ${workflowId}`);
        const response = await snowflakeApi.get(`/api/v1/workflows/${workflowId}/module-status`);
        
        console.log('Module statuses response:', response.data);
        setModuleStatuses(response.data);
        
        // Also update the context with module statuses
        if (setContextModuleStatuses) {
          setContextModuleStatuses(response.data);
        }              
      } catch (error) {
        console.error('Error fetching module statuses:', error);
        // Set default state on error
        setModuleStatuses({
          workflow_id: 0,
          modules: [],
          latest_status_by_type: {},
          step_statuses: {},
          error: error instanceof Error ? error.message : 'Failed to fetch module statuses'
        });
      } finally {
        setIsLoadingModuleStatuses(false);
      }
    };

    fetchModuleStatuses();
  }, [workflowId, refreshTrigger]); // Re-run when workflowId or refreshTrigger changes
  
  const handleStepClick = async (step: number) => {
    // Get current steps array
    const steps = getSteps(moduleStatuses);
    
    // Check if module4-config exists - if so, consider steps 1-4 as accessible
    const hasModule4Config = moduleStatuses?.latest_status_by_type?.['module4-config'];
    // Check if module3 exists - if so, consider steps 2-3 as accessible
    const hasModule3 = moduleStatuses?.latest_status_by_type?.['module3'];
    
    // Check if the step is clickable (inverse of disabled)
    const isStepClickable = !isStepDisabled(step, workflowId, moduleStatuses, hasModule4Config, hasModule3);

    // Check if the current step is clickable
    if (isStepClickable) {
      let path = `/${steps[step].path}`;
      
      if (step === 4) { // Job monitoring/Batch Results - check for job availability
        try {
          // Fetch job availability to get parameters for navigation
          console.log(`Fetching job availability for completed workflow: ${workflowId}`);
          const response = await snowflakeApi.get(`/api/v1/workflows/${workflowId}/job-availability`);
          const jobAvailability = response.data;
          
          if (jobAvailability.hasJobs && jobAvailability.jobParams) {
            // Use job availability parameters to construct the URL
            const { externalJobId, moduleId: jobModuleId, workflowId: numericWorkflowId, clientId: jobClientId } = jobAvailability.jobParams;
            
            // Build the query string with job parameters
            const queryParams = [
              `moduleId=${jobModuleId}`,
              `workflowId=${numericWorkflowId}`,
              `clientId=${jobClientId}`
            ];
            
            const queryString = `?${queryParams.join('&')}`;
            
            // Navigate to jobs with the external job ID and query params
            path = `/jobs/${externalJobId}${queryString}`;
          } else {
            console.warn('Job availability check returned no jobs despite completed status');
          }
        } catch (error) {
          console.error('Error fetching job availability:', error);
          // Fallback to basic path if job availability fails
        }
      } else if (step === 5 || step === 6 || step === 7) { // SERP analysis, DataForSEO or Explore with workflow context
        if (clientId && workflowId) {
          path = `/${steps[step].path}/client/${clientId}/${workflowId}`;
        } else if (workflowId) {
          path = `/${steps[step].path}/${workflowId}`;
        }
      } else if (clientId && workflowId) {
        path = `/${steps[step].path}/client/${clientId}/${workflowId}`;
      } else if (workflowId) {
        path = `/${steps[step].path}/${workflowId}`;
      } else if (clientId) {
        path = `/${steps[step].path}/client/${clientId}`;
      }
      
      navigate(path);
    }
  };

  const toggleCollapsed = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    
    // Persist to localStorage
    try {
      localStorage.setItem('workflowStepperCollapsed', newCollapsedState.toString());
    } catch (error) {
      console.warn('Failed to write to localStorage:', error);
    }
    
    // Notify parent component about collapse state change
    if (onCollapseChange) {
      onCollapseChange(newCollapsedState);
    }
  };

  if (isCollapsed) {
    // Get dynamic steps
    const steps = getSteps(moduleStatuses);
    
    return (
      <CollapsedStepperContainer elevation={2}>
        <Tooltip title="Expand Workflow Steps" placement="right">
          <StyledIconButton 
            onClick={toggleCollapsed} 
            sx={{ 
              mb: 2, 
              color: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              }
            }}
          >
            <ExpandIcon />
          </StyledIconButton>
        </Tooltip>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          {steps.map((step, index) => {
            // Check if module4-config exists - if so, consider steps 1-4 as complete
            const hasModule4Config = moduleStatuses?.latest_status_by_type?.['module4-config'];
            // Check if module3 exists - if so, consider steps 2-3 as complete
            const hasModule3 = moduleStatuses?.latest_status_by_type?.['module3'];
            
            // Get step completion status from module statuses
            const stepStatus = moduleStatuses?.step_statuses?.[String(index + 1)];
            const stepCompleted = stepStatus?.completed || 
                                 (hasModule4Config && index <= 3) || 
                                 (hasModule3 && (index === 1 || index === 2)) || // Steps 2-3 (index 1-2) are complete if module3 exists
                                 false;
            const stepInProgress = stepStatus?.in_progress || false;
            
            // Determine if this step should be disabled using the helper function
            const isDisabled = isStepDisabled(index, workflowId, moduleStatuses, hasModule4Config, hasModule3);
            
            const tooltipTitle = stepInProgress
              ? `${step.label}: Processing...`
              : isDisabled && index === 0
                ? `${step.label} requires workflow context`
                : isDisabled && index === 4 
                  ? "Complete previous steps and ensure batch processing is finished" 
                : isDisabled
                  ? `Complete step ${index} (${steps[index - 1]?.label || 'previous step'}) first`
                  : `${step.label}: ${step.description}`;
            
            return (
              <Tooltip 
                key={step.label} 
                title={tooltipTitle}
                placement="right"
              >
                <span> {/* Wrap in span to allow tooltip on disabled button */}
                  <StyledIconButton 
                    color={index === activeStep ? "primary" : "default"}
                    onClick={() => handleStepClick(index)}
                    onMouseEnter={() => setHoveredStep(index)}
                    onMouseLeave={() => setHoveredStep(null)}
                    disabled={isDisabled}
                    sx={{
                      backgroundColor: index === activeStep 
                        ? alpha(theme.palette.primary.main, 0.12)
                        : stepInProgress
                          ? alpha(theme.palette.warning.main, 0.08)
                        : hoveredStep === index && !isDisabled
                          ? alpha(theme.palette.primary.main, 0.04)
                          : 'transparent',
                      padding: '10px',
                      '& svg': {
                        color: isDisabled
                          ? theme.palette.action.disabled
                          : stepInProgress
                            ? theme.palette.warning.main
                          : index === activeStep 
                            ? theme.palette.primary.main 
                            : stepCompleted 
                              ? theme.palette.success.dark 
                              : theme.palette.text.secondary,
                        transition: theme.transitions.create(['color'], {
                          duration: theme.transitions.duration.shorter,
                        }),
                      },
                      ...(stepCompleted && !stepInProgress && {
                        border: `2px solid ${theme.palette.success.dark}`,
                        backgroundColor: alpha(theme.palette.success.light, 0.1),
                      }),
                      ...(stepInProgress && {
                        border: `2px solid ${theme.palette.warning.main}`,
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      }),
                      ...(isDisabled && {
                        opacity: 0.6,
                        cursor: 'not-allowed',
                      }),
                    }}
                  >
                    <ProcessingStepIcon 
                      icon={step.icon} 
                      isProcessing={stepInProgress}
                      size={20}
                    />
                  </StyledIconButton>
                </span>
              </Tooltip>
            );
          })}
        </Box>
      </CollapsedStepperContainer>
    );
  }

  // Get dynamic steps for expanded view
  const steps = getSteps(moduleStatuses);

  return (
    <VerticalStepperContainer elevation={2} sx={sx}>
      <Box sx={{ 
        mb: 4, 
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        pb: 1
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center'
        }}>
          <GradientTypography variant="h6" sx={{ fontSize: '1.25rem' }}>
            Workflow Progress
          </GradientTypography>
          <Tooltip title="Collapse Workflow Steps" placement="left">
            <StyledIconButton 
              onClick={toggleCollapsed} 
              size="small"
              sx={{ 
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                }
              }}
            >
              <CollapseIcon />
            </StyledIconButton>
          </Tooltip>
        </Box>
        <Box sx={{ mt: 0.5, minHeight: '36px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          {(clientName || clientId) && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontWeight: 400,
                fontSize: '0.8rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {clientName || 'Loading...'} ({clientId || '...'})
            </Typography>
          )}
          {(workflowName || workflowId) && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontWeight: 400,
                fontSize: '0.8rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {workflowName || 'Loading...'} ({workflowId || '...'})
            </Typography>
          )}
        </Box>
      </Box>
      
      <Stepper 
        activeStep={activeStep} 
        orientation="vertical"
        nonLinear
        sx={{ 
          width: '100%',
          '& .MuiStepConnector-line': {
            borderLeftWidth: '2px',
            borderColor: alpha(theme.palette.primary.main, 0.2),
            minHeight: '40px',
          },
          '& .MuiStepLabel-iconContainer': {
            bgcolor: 'transparent',
          },
          '& .Mui-completed': {
            '& .MuiStepConnector-line': {
              borderColor: theme.palette.success.dark,
            },
            '& .MuiStepIcon-root': {
              color: theme.palette.success.dark,
              '& .MuiStepIcon-text': {
                fill: 'white',
              }
            }
          },
          '& .step-processing': {
            '& .MuiStepIcon-root': {
              color: theme.palette.warning.main,
              '& .MuiStepIcon-text': {
                fill: 'white',
              }
            },
            '& .MuiStepConnector-line': {
              borderColor: theme.palette.warning.main,
            }
          },
          '& .Mui-active': {
            '& .MuiStepConnector-line': {
              borderColor: theme.palette.primary.main,
            }
          },
        }}
      >
        {steps.map((step, index) => {
          const stepProps: { completed?: boolean } = {};
          
          // Check if module4-config exists - if so, consider steps 1-4 as complete
          const hasModule4Config = moduleStatuses?.latest_status_by_type?.['module4-config'];
          // Check if module3 exists - if so, consider steps 2-3 as complete
          const hasModule3 = moduleStatuses?.latest_status_by_type?.['module3'];
          
          // Get step completion status from module statuses
          const stepStatus = moduleStatuses?.step_statuses?.[String(index + 1)];
          stepProps.completed = stepStatus?.completed || 
                               (hasModule4Config && index <= 3) || 
                               (hasModule3 && (index === 1 || index === 2)) || // Steps 2-3 (index 1-2) are complete if module3 exists
                               false;
          const stepInProgress = stepStatus?.in_progress || false;
          
          // Determine if this step should be disabled using the helper function
          const isDisabled = isStepDisabled(index, workflowId, moduleStatuses, hasModule4Config, hasModule3);
          
          return (
            <Step 
              key={step.label} 
              {...stepProps}
              className={stepInProgress ? 'step-processing' : undefined}
            >
              {stepInProgress && (
                <StepLabel
                  onClick={() => handleStepClick(index)}
                  StepIconComponent={() => (
                    <CustomStepIcon
                      icon={step.icon}
                      active={index === activeStep}
                      completed={stepProps.completed || false}
                      inProgress={stepInProgress}
                      stepNumber={index + 1}
                    />
                  )}
                  sx={{
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    ml: -1,
                    borderRadius: '4px',
                    padding: theme.spacing(1),
                    transition: theme.transitions.create(['background-color'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                    '&:hover': {
                      backgroundColor: isDisabled 
                        ? 'transparent'
                        : alpha(theme.palette.warning.main, 0.08),
                    },
                    ...(index === activeStep && {
                      backgroundColor: alpha(theme.palette.warning.main, 0.08),
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.warning.main, 0.3)}`,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.warning.main, 0.12),
                      },
                    }),
                    ...(isDisabled && {
                      opacity: 0.7,
                      cursor: 'not-allowed',
                    }),
                  }}
                >
                  <Typography 
                    sx={{ 
                      fontWeight: index === activeStep ? 600 : 400,
                      color: theme.palette.warning.main
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: theme.palette.warning.main
                    }}
                  >
                    {step.description}
                  </Typography>
                </StepLabel>
              )}
              {!stepInProgress && (
                <StepButton 
                onClick={() => handleStepClick(index)}
                optional={
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: isDisabled 
                        ? theme.palette.text.disabled
                        : stepInProgress
                          ? theme.palette.warning.main
                        : index === activeStep 
                          ? theme.palette.primary.main 
                          : theme.palette.text.secondary
                    }}
                  >
                    {step.description}
                  </Typography>
                }
                disabled={isDisabled}
                sx={{
                  borderRadius: '4px',
                  transition: theme.transitions.create(['background-color'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  '&:hover': {
                    backgroundColor: isDisabled 
                      ? 'transparent'
                      : stepInProgress
                        ? alpha(theme.palette.warning.main, 0.08)
                      : alpha(theme.palette.primary.main, 0.04),
                  },
                  ...(index === activeStep && {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    },
                    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                  }),
                  ...(stepProps.completed && !stepInProgress && {
                    backgroundColor: alpha(theme.palette.success.dark, 0.04),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.success.dark, 0.08),
                    },
                  }),
                  ...(stepInProgress && {
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.warning.main, 0.12),
                    },
                    boxShadow: `0 0 0 1px ${alpha(theme.palette.warning.main, 0.3)}`,
                  }),
                  ...(isDisabled && {
                    opacity: 0.7,
                    cursor: 'not-allowed',
                  }),
                }}
              >
                <Typography 
                  sx={{ 
                    fontWeight: index === activeStep ? 600 : 400,
                    color: isDisabled
                      ? theme.palette.text.disabled
                      : stepInProgress
                        ? theme.palette.warning.main
                      : index === activeStep 
                        ? theme.palette.primary.main 
                        : stepProps.completed 
                          ? theme.palette.success.dark 
                          : theme.palette.text.primary
                  }}
                >
                  {step.label}
                </Typography>
                </StepButton>
              )}
            </Step>
          );
        })}
      </Stepper>
    </VerticalStepperContainer>
  );
};

export default WorkflowStepper;
